import { Container } from '@components/ui/Container';
import { Section } from '@components/ui/Section';
import { ButtonAnchor } from '@components/ui/Button/ButtonAnchor';
import { ScrollReveal } from '@components/motion/ScrollReveal';
import { StaggerContainer, StaggerItem } from '@components/motion/StaggerContainer';
import { FadeIn } from '@components/motion/FadeIn';
import {
  Eyebrow,
  H1,
  H2,
  Lead,
  Body,
  GradientText,
  SectionHeader,
} from '@components/ui/Typography';
import { ROUTES } from '@constants/routes';
import { COMPANY, whatsappUrl } from '@constants/company';
import {
  HeroSection,
  HeroGrid,
  HeroGlow,
  HeroContent,
  TimelineWrapper,
  TimelineItem,
  TimelineDot,
  TimelineDotRight,
  TimelineCard,
  TimelineYear,
  TimelineTitle,
  TimelineText,
  MvvGrid,
  MvvCard,
  MvvIcon,
  MvvTitle,
  MvvText,
  DiferenciaisGrid,
  DiferencialCard,
  DiferencialIcon,
  DiferencialTitle,
  DiferencialText,
  CtaSection,
  CtaGlow,
  CtaContent,
  CtaButtons,
} from './styles';
import { memo } from 'react';
// import { PageTransition } from '@/components';

/* ── Dados ─────────────────────────────────────────────────── */

const timeline = [
  {
    year: '2000',
    side: 'left' as const,
    title: 'O começo de tudo',
    text: 'A Teia de Eventos nasce em Nova Friburgo com um propósito claro: organizar eventos que realmente movem a cidade. O primeiro grande projeto já mostrava o que viria pela frente.',
  },
  {
    year: '2001',
    side: 'right' as const,
    title: 'Consolidação regional',
    text: 'Ao longo da primeira década, a empresa consolida sua presença na Região Serrana, tornando-se referência em organização de feiras, festas culturais e eventos corporativos.',
  },
  {
    year: '2010',
    side: 'left' as const,
    title: 'Expansão do portfólio',
    text: 'Novos eventos entram para o calendário — a Festa do Morango com Chocolate, a Festa da Flor e o Festival de Comida de Boteco ganham vida e se tornam tradição em Nova Friburgo.',
  },
  {
    year: '2024',
    side: 'right' as const,
    title: 'Nossa Feira',
    text: 'Nasce a Nossa Feira, um evento que foi criado a partir do desejo de trazer a Nova Friburgo, um evento acessível, autêntico e que movimenta o comércio local. E já na sua primeira edição surpreendeu ao movimentar milhares de pessoas e conectar negócios de diferentes setores',
  },
  {
    year: 'Hoje',
    side: 'left' as const,
    title: '26 anos e contando',
    text: 'Mais de 100 mil visitantes por ano, mais de 15 eventos no calendário e centenas de expositores atendidos. A Teia de Eventos segue crescendo, inovando e conectando pessoas.',
  },
];

const mvv = [
  {
    icon: '🎯',
    title: 'Missão',
    text: 'Criar eventos que conectam pessoas, geram negócios e celebram a cultura da Região Serrana — com planejamento rigoroso, execução impecável e paixão por cada detalhe.',
    accent: 'linear-gradient(90deg, #183C54, #186C9C)',
  },
  {
    icon: '🔭',
    title: 'Visão',
    text: 'Ser reconhecida como a maior e mais respeitada empresa de eventos do interior do Rio de Janeiro, referência em inovação, experiência e impacto econômico regional.',
    accent: 'linear-gradient(90deg, #186C9C, #00A8F0)',
  },
  {
    icon: '⭐',
    title: 'Valores',
    text: 'Comprometimento com o resultado. Respeito com expositores, parceiros e visitantes. Criatividade na solução de desafios. Orgulho de ser de Nova Friburgo.',
    accent: 'linear-gradient(90deg, #00A8F0, #3BBAF3)',
  },
];

const diferenciais = [
  {
    icon: '🏆',
    title: '26 anos de experiência',
    text: 'Mais de duas décadas organizando eventos de todos os portes — da logística ao atendimento, sabemos o que funciona.',
  },
  {
    icon: '🤝',
    title: 'Foco no expositor',
    text: 'Nossa prioridade é o sucesso de quem expõe. Cada evento é planejado para maximizar visibilidade e geração de negócios.',
  },
  {
    icon: '📍',
    title: 'Raízes na região',
    text: 'Somos de Nova Friburgo. Conhecemos o público, os parceiros e as oportunidades da Região Serrana como ninguém.',
  },
  {
    icon: '🎪',
    title: 'Portfólio diverso',
    text: 'De feiras de negócios a festas culturais e gastronômicas — atendemos diferentes públicos e segmentos com a mesma excelência.',
  },
  {
    icon: '📈',
    title: 'Impacto econômico real',
    text: 'Nossos eventos movimentam a economia local, atraem turistas e fortalecem o comércio da cidade durante todo o ano.',
  },
];

/* ═══════════════════════════════════════════════════════════
   PÁGINA QUEM SOMOS
══════════════════════════════════════════════════════════════ */

const About: React.FC = () => {
  const whatsappMsg = 'Olá! Gostaria de saber mais sobre a Teia de Eventos.';

  return (
    <>
      <title>Quem Somos — Teia de Eventos</title>
      <meta
        name="description"
        content="Conheça a história da Teia de Eventos — 26 anos conectando pessoas, gerando negócios e celebrando a cultura de Nova Friburgo e região."
      />

      {/* HERO */}
      <HeroSection>
        <HeroGrid />
        <HeroGlow />
        <HeroContent>
          <FadeIn direction="up" delay={0.1}>
            <Eyebrow>Nossa história</Eyebrow>
          </FadeIn>
          <FadeIn direction="up" delay={0.25}>
            <H1 $inverse>
              26 anos criando <GradientText>experiências reais.</GradientText>
            </H1>
          </FadeIn>
          <FadeIn direction="up" delay={0.4}>
            <Lead $inverse>
              A Teia de Eventos nasceu em Nova Friburgo em 2000 com um propósito simples e poderoso:
              transformar ideias em eventos que ficam na memória. Mais de duas décadas depois,
              seguimos com o mesmo espírito — e muito mais experiência.
            </Lead>
          </FadeIn>
        </HeroContent>
      </HeroSection>

      {/* HISTÓRIA / TIMELINE */}
      <Section $bg="primary" $spacing="xl">
        <Container>
          <ScrollReveal>
            <SectionHeader>
              <Eyebrow>Trajetória</Eyebrow>
              <H2>
                Uma história construída <GradientText>evento por evento</GradientText>
              </H2>
              <Lead>
                Cada edição, cada expositor, cada visitante fez parte da construção de quem somos
                hoje.
              </Lead>
            </SectionHeader>
          </ScrollReveal>

          <TimelineWrapper>
            {timeline.map((item, i) => (
              <TimelineItem
                key={item.year}
                $side={item.side}
                initial={{ opacity: 0, x: item.side === 'left' ? -40 : 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.05 }}
              >
                {item.side === 'left' ? <TimelineDot /> : <TimelineDotRight />}
                <TimelineCard>
                  <TimelineYear>{item.year}</TimelineYear>
                  <TimelineTitle>{item.title}</TimelineTitle>
                  <TimelineText>{item.text}</TimelineText>
                </TimelineCard>
              </TimelineItem>
            ))}
          </TimelineWrapper>
        </Container>
      </Section>

      {/* MISSÃO, VISÃO E VALORES */}
      <Section $bg="secondary" $spacing="xl">
        <Container>
          <ScrollReveal>
            <SectionHeader $centered>
              <Eyebrow>Nossa essência</Eyebrow>
              <H2>
                O que nos <GradientText>move</GradientText>
              </H2>
              <Lead style={{ textAlign: 'center', margin: '0 auto' }}>
                Por trás de cada evento há um propósito claro que guia cada decisão, cada parceria e
                cada entrega.
              </Lead>
            </SectionHeader>
          </ScrollReveal>

          <MvvGrid>
            <StaggerContainer staggerDelay={0.12} initialDelay={0.1}>
              {mvv.map((item) => (
                <StaggerItem key={item.title}>
                  <MvvCard
                    $accent={item.accent}
                    whileHover={{ y: -4 }}
                    transition={{ duration: 0.3 }}
                  >
                    <MvvIcon>{item.icon}</MvvIcon>
                    <MvvTitle>{item.title}</MvvTitle>
                    <MvvText>{item.text}</MvvText>
                  </MvvCard>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </MvvGrid>
        </Container>
      </Section>

      {/* DIFERENCIAIS */}
      <Section $bg="primary" $spacing="xl">
        <Container>
          <ScrollReveal>
            <SectionHeader>
              <Eyebrow>Por que a Teia</Eyebrow>
              <H2>
                O que nos torna <GradientText>diferentes</GradientText>
              </H2>
              <Lead>
                Experiência, compromisso regional e um portfólio diverso que atende do expositor ao
                visitante com a mesma dedicação.
              </Lead>
            </SectionHeader>
          </ScrollReveal>

          <DiferenciaisGrid>
            <StaggerContainer staggerDelay={0.08} initialDelay={0.1}>
              {diferenciais.map((item) => (
                <StaggerItem key={item.title}>
                  <DiferencialCard whileHover={{ y: -3 }} transition={{ duration: 0.25 }}>
                    <DiferencialIcon>{item.icon}</DiferencialIcon>
                    <DiferencialTitle>{item.title}</DiferencialTitle>
                    <DiferencialText>{item.text}</DiferencialText>
                  </DiferencialCard>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </DiferenciaisGrid>
        </Container>
      </Section>

      {/* CTA */}
      <CtaSection>
        <CtaGlow />
        <CtaContent>
          <FadeIn direction="up">
            <Eyebrow style={{ justifyContent: 'center' }}>Vamos trabalhar juntos</Eyebrow>
          </FadeIn>
          <FadeIn direction="up" delay={0.1}>
            <H2 $inverse style={{ textAlign: 'center', maxWidth: '20ch', margin: '0 auto' }}>
              Faça parte da nossa história.
            </H2>
          </FadeIn>
          <FadeIn direction="up" delay={0.2}>
            <Lead $inverse style={{ textAlign: 'center', margin: '0 auto' }}>
              Seja como expositor, patrocinador ou parceiro — há espaço para quem quer crescer junto
              com a Região Serrana.
            </Lead>
          </FadeIn>
          <FadeIn direction="up" delay={0.3}>
            <CtaButtons>
              <ButtonAnchor
                href={whatsappUrl(whatsappMsg)}
                target="_blank"
                rel="noopener noreferrer"
                $variant="primary"
                $size="lg"
                whileTap={{ scale: 0.96 }}
              >
                Falar pelo WhatsApp
              </ButtonAnchor>
              <ButtonAnchor
                href={ROUTES.events}
                $variant="outline"
                $size="lg"
                style={{ color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.3)' }}
              >
                Ver nossos eventos
              </ButtonAnchor>
            </CtaButtons>
          </FadeIn>
          <FadeIn direction="none" delay={0.4}>
            <Body $size="sm" $inverse style={{ opacity: 0.4, textAlign: 'center' }}>
              {COMPANY.address.city}, {COMPANY.address.state} · {COMPANY.contact.phone}
            </Body>
          </FadeIn>
        </CtaContent>
      </CtaSection>
    </>
  );
};

export default memo(About);
